import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  ModalService,
  ProxyService,
  AuthenticationService,
  ValidationService,
  AlertService,
  AuthTrackingService,
  AsyncValidatorWExt,
} from '@core/services';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { ErrorCodes } from '@shared/models/error-codes/response.error-codes';
import { Constants } from '@core/constants/core.constants';
import { SurveyService } from '@core/services/survey.service';
import {
  applyAuth0OtpErrorUiState,
  resetAuth0OtpErrorUiState,
  resolveAuth0OtpHttpError,
} from '@features/authenticate/auth0-otp-error.util';

type Step = 'email' | 'otpConfirm' | 'otpVerify' | 'social' | 'signInInstead';

interface Auth0SignupCheckResponse {
  canSignup: boolean;
  reason?: string;
  providers?: string[];
  signInHint?: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  'google-oauth2': 'Google',
  apple: 'Apple',
  facebook: 'Facebook',
  facebookoauth2: 'Facebook',
};

function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) {
    return email;
  }
  const local = email.slice(0, at);
  const domain = email.slice(at);
  if (local.length <= 1) {
    return `*${domain}`;
  }
  return `${local[0]}***${domain}`;
}

@Component({
  selector: 'app-auth0-email-signup',
  templateUrl: './auth0-email-signup.component.html',
  styleUrls: ['../register/register.component.less', '../identifier-first-signin/identifier-first-signin.component.less'],
  providers: [AlertService],
})
export class Auth0EmailSignupComponent implements OnInit, OnDestroy {
  step: Step = 'email';
  emailForm: UntypedFormGroup;
  otpForm: UntypedFormGroup;

  emailValue = '';
  channel = 'email';
  maskedIdentifier = '';
  identifierValue = '';
  socialProviders: string[] = [];
  blockedMessage = '';

  loading = false;
  disableContinue = true;
  errorMessage = '';
  otpRateLimitBanner = '';
  otpContinueDisabled = false;
  otpResendDisabled = false;
  otpShowContactUs = false;
  returnUrl = '';

  private subscriptions = new Subscription();

  constructor(
    public alertService: AlertService,
    private fb: UntypedFormBuilder,
    private modalService: ModalService,
    private proxyService: ProxyService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private authTracking: AuthTrackingService,
    private survey: SurveyService,
    private cdr: ChangeDetectorRef
  ) {
    this.authService.setAlertService(this.alertService);
    this.emailForm = this.fb.group({
      email: new UntypedFormControl(null, [Validators.required], [
        AsyncValidatorWExt.debounce(ValidationService.asyncEmailValidator, 800),
      ]),
    });
    this.otpForm = this.fb.group({
      otpCode: new UntypedFormControl('', [Validators.required, Validators.minLength(6)]),
    });
  }

  ngOnInit(): void {
    this.subscriptions.add(this.emailForm.valueChanges.subscribe(() => this.updateContinueButton()));
    this.subscriptions.add(this.emailForm.statusChanges.subscribe(() => this.updateContinueButton()));
    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        this.returnUrl = (params[Constants.RETURN_URL] as string) || '';
        if (this.returnUrl) {
          this.returnUrl = decodeURIComponent(this.returnUrl);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateContinueButton(): void {
    this.disableContinue = !this.emailForm.valid;
    this.cdr.markForCheck();
  }

  onEmailContinue(): void {
    this.errorMessage = '';
    const email = this.emailForm.get('email')?.value?.trim();
    if (!email) {
      return;
    }
    this.emailValue = email;
    this.loading = true;
    this.disableContinue = true;

    const body = { email, channel: 'email' };
    this.proxyService.httpPost(ApiRoutes.AUTH0_SIGNUP_CHECK, body).subscribe(
      (resp: unknown) => {
        const res = (resp as { body?: Auth0SignupCheckResponse })?.body ?? (resp as Auth0SignupCheckResponse);
        this.loading = false;
        this.disableContinue = false;
        if (!res) {
          this.errorMessage = 'Could not verify signup eligibility. Please try again.';
          return;
        }
        if (res.canSignup) {
          this.identifierValue = email;
          this.channel = 'email';
          this.maskedIdentifier = maskEmail(email);
          this.step = 'otpConfirm';
          return;
        }
        this.socialProviders = res.providers ?? [];
        if (res.reason === 'already_social') {
          this.step = 'social';
          return;
        }
        if (res.reason === 'already_passwordless') {
          this.blockedMessage =
            'An account with this email already exists. Sign in with a one-time code or magic link instead of creating a new account.';
          this.step = 'signInInstead';
          return;
        }
        this.blockedMessage =
          'An account with this email already exists. Sign in with your email and password, or use the sign-in options below.';
        this.step = 'signInInstead';
      },
      () => {
        this.loading = false;
        this.disableContinue = false;
        this.errorMessage = 'Could not verify signup eligibility. Please try again.';
      }
    );
  }

  onOtpSendCode(): void {
    resetAuth0OtpErrorUiState(this);
    this.loading = true;
    const payload: Record<string, string> = {
      identifier: this.identifierValue,
      channel: this.channel,
      send: 'code',
      intent: 'signup',
    };
    this.proxyService.httpPost(ApiRoutes.AUTH0_OTP_INITIATE, payload).subscribe(
      () => {
        this.loading = false;
        this.step = 'otpVerify';
        this.otpForm.reset();
      },
      (err: { error?: { code?: number; message?: string } }) => {
        this.loading = false;
        if (err?.error?.code === ErrorCodes.AUTH0_SIGNUP_NOT_ALLOWED) {
          this.errorMessage =
            err.error?.message ??
            'This email cannot be used for a new signup. Try signing in or use a different email.';
          return;
        }
        this.handleOtpHttpError(err, 'Failed to send code. Please try again.');
      }
    );
  }

  onOtpVerify(): void {
    resetAuth0OtpErrorUiState(this);
    const raw = this.otpForm.get('otpCode')?.value ?? '';
    const code = String(raw).replace(/\D/g, '').slice(0, 6);
    if (!code || code.length < 6) {
      this.errorMessage = 'Please enter the 6-digit code.';
      return;
    }
    this.loading = true;
    const payload: Record<string, string> = {
      identifier: this.identifierValue,
      otpCode: code,
      channel: this.channel,
    };
    this.proxyService.httpPost(ApiRoutes.AUTH0_OTP_VERIFY, payload).subscribe(
      (response: unknown) => {
        const body = (response as { body?: unknown })?.body ?? response;
        this.authService.setAuth0TokensFromOtpVerify(body, () => {
          this.modalService.hideModal();
        });
        this.loading = false;
        this.onAuthSuccess();
      },
      (err: { error?: { message?: string; code?: number } }) => {
        this.loading = false;
        this.handleOtpHttpError(err, 'Invalid code. Please try again.');
      }
    );
  }

  private handleOtpHttpError(
    err: { status?: number; error?: { code?: number; message?: string } },
    fallbackMessage: string
  ): void {
    const state = resolveAuth0OtpHttpError(err, fallbackMessage);
    applyAuth0OtpErrorUiState(state, this);
  }

  async onSocialClick(provider: string): Promise<void> {
    this.loading = true;
    this.alertService.clear();
    this.cdr.detectChanges();
    const p = (provider ?? '').toLowerCase();
    if (p.includes('google')) {
      await this.authService.googleRegister(false, true);
      if (this.authService.getErrorMessage() === null) {
        this.onAuthSuccess();
      } else {
        this.alertService.error(this.authService.getErrorMessage());
      }
    } else if (p.includes('facebook')) {
      await this.authService.facebookRegister(false, true);
      if (this.authService.facebookProfile && this.authService.doesAccountPreexist) {
        await this.authService.completeRegistration();
        if (this.authService.getErrorMessage() === null) {
          this.onAuthSuccess();
        } else {
          this.alertService.error(this.authService.getErrorMessage());
        }
      } else if (this.authService.getErrorMessage()) {
        this.alertService.error(this.authService.getErrorMessage());
      }
    } else if (p.includes('apple')) {
      this.authService.appleAuth('signIn');
    } else {
      await this.authService.googleRegister(false, true);
      if (this.authService.getErrorMessage() === null) {
        this.onAuthSuccess();
      } else {
        this.alertService.error(this.authService.getErrorMessage());
      }
    }
    this.loading = false;
    this.cdr.detectChanges();
  }

  onAuthSuccess(): void {
    this.survey.isQualtricsLoaded = false;
    this.modalService.hideModal();
    if (this.authTracking.isLoggedIn() && this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    }
  }

  onBack(): void {
    this.step = 'email';
    resetAuth0OtpErrorUiState(this);
    this.blockedMessage = '';
    this.otpForm.reset();
  }

  onResendOtp(): void {
    if (this.loading || this.otpResendDisabled) {
      return;
    }
    this.onOtpSendCode();
  }

  openSignInModal(): void {
    this.modalService.hideModal();
    setTimeout(() => {
      if (this.returnUrl) {
        this.router.navigate(['/home'], {
          queryParams: { [Constants.RETURN_URL]: this.returnUrl },
        });
      }
    }, 300);
    setTimeout(() => {
      this.modalService.setModalSelector(this.modalService.getSignInModalSelector());
      this.modalService.showModal(true);
    }, 500);
  }

  openRegisterModal(): void {
    this.modalService.hideModal();
    setTimeout(() => {
      this.modalService.setModalSelector(this.modalService.getSignupModalSelector());
      this.modalService.showModal(false);
    }, 300);
  }

  getProviderLabel(provider: string): string {
    return PROVIDER_LABELS[provider?.toLowerCase()] ?? provider;
  }

  hasProvider(name: string): boolean {
    return this.socialProviders.some((p) => (p ?? '').toLowerCase().includes(name.toLowerCase()));
  }
}
